/**
 * usePayerContracts Hook
 *
 * Loads payer contracts from the API via RTK Query.
 */

import { useState, useMemo } from 'react';
import type { PayerContract } from '../types';
import { useGetPayerDocsQuery } from '../store/api';

/**
 * Hook to load and manage payer contracts
 */
export function usePayerContracts() {
  const { data: apiData, isLoading, isError } = useGetPayerDocsQuery();

  // Extract PayerContract payloads from the API response
  const payers = useMemo(() => {
    if (!apiData?.items) return [];
    return apiData.items
      .filter(item => item.payload != null)
      .map(item => item.payload as unknown as PayerContract);
  }, [apiData]);

  // Start on overview (no payer selected) so the card grid is the landing
  const [selectedPayerId, setSelectedPayerId] = useState<string | null>(null);

  const selectedPayer = useMemo(() => {
    if (!selectedPayerId) return null;
    return payers.find(p => p.payer.name === selectedPayerId) || null;
  }, [payers, selectedPayerId]);

  const getPayer = (id: string): PayerContract | undefined => {
    return payers.find(p => p.payer.name === id);
  };

  return {
    payers,
    selectedPayer,
    selectedPayerId,
    setSelectedPayerId: (id: string | null) => setSelectedPayerId(id),
    getPayer,
    isLoading,
    isError,
  };
}
